"use client";


import React, { useState } from 'react';
import { AgentMessage, asContent } from '@/lib/state/agent-sync';
import SmartCardUI from '@/components/smartcard-ui';
import { Button } from '@/components/ui/button';
import { Clock, ChevronRight, Check, X, CalendarClock, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Command,
    CommandGroup,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { cn } from '@/lib/utils';

interface AgentActionDeckProps {
    messages: AgentMessage[];
    onAction: (messageId: string, actionId: string, values: any) => void;
    onSnooze: (messageId: string, until: number) => void;
}

export function AgentActionDeck({ messages, onAction, onSnooze }: AgentActionDeckProps) {
    // Filter for messages that have Smart Card recipes and are NOT snoozed/archived
    const activeCards = messages.filter(m =>
        m.sender === 'agent' &&
        asContent(m.content)?.recipe &&
        !['snoozed', 'archived'].includes(m.status)
    );

    // State for card values (if needed for parent tracking)
    const [cardValues, setCardValues] = useState<Record<string, any>>({});

    const handleValuesChange = (msgId: string, values: any) => {
        setCardValues(prev => ({
            ...prev,
            [msgId]: values
        }));
    };

    if (activeCards.length === 0) {
        return (
            <div className="flex flex-col h-full items-center justify-center p-8 text-center border-l border-white/5 bg-[#1E1F20]">
                <div className="h-12 w-12 rounded-full bg-white/5 flex items-center justify-center mb-4">
                    <Check className="h-6 w-6 text-green-400/50" />
                </div>
                <h3 className="text-lg font-medium text-white/80 mb-2">All Caught Up</h3>
                <p className="text-sm text-white/40">No pending reviews from Heather.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full border-l border-white/5 bg-[#1E1F20] w-full">
            <div className="p-4 border-b border-white/5 flex items-center justify-center shrink-0">
                <h2 className="text-2xl md:text-3xl font-bold text-[#E3E3E3] uppercase tracking-wider flex items-center gap-2">
                    Pending Reviews
                    <span className="text-red-500 font-black text-4xl md:text-5xl">
                        ({activeCards.length})
                    </span>
                </h2>
            </div>

            <div className="flex-1 overflow-y-auto p-4 pb-12 flex flex-col gap-4 custom-scrollbar">
                {activeCards.map((msg, index) => (
                    <div key={msg.id} className="w-full flex flex-col relative group animate-in fade-in slide-in-from-right-4 duration-300 fill-mode-backwards" style={{ animationDelay: `${index * 100}ms` }}>
                        {/* The Smart Card */}
                        <div className="bg-[#28292a] rounded-xl overflow-hidden border border-white/5 shadow-sm flex flex-col">
                            <div className="p-4">
                                {asContent(msg.content)?.recipe && (
                                    <SmartCardUI
                                        recipe={asContent(msg.content)!.recipe!}
                                        onAction={(act, vals) => {
                                            if (onAction) onAction(msg.id!, act, vals);
                                        }}
                                        onValuesChange={(vals) => handleValuesChange(msg.id!, vals)}
                                        disabled={msg.status !== 'draft'} // Disable inputs if not draft
                                        hideActions={true} // We provide custom footer
                                    />
                                )}
                            </div>

                            {/* Custom Footer for Agent Actions */}
                            <div className="p-3 bg-[#1E1F20]/50 border-t border-white/5">
                                {msg.status === 'draft' ? (
                                    <div className="grid grid-cols-3 gap-2">
                                        {/* Action Buttons (Approve/Reject/Postpone) */}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="flex flex-col items-center gap-1 h-auto py-2 hover:bg-green-500/10 hover:text-green-400 text-gray-400"
                                            onClick={() => onAction && onAction(msg.id!, 'approve', cardValues[msg.id!] || {})}
                                        >
                                            <Check className="h-4 w-4" />
                                            <span className="text-[10px]">Accept</span>
                                        </Button>

                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="flex flex-col items-center gap-1 h-auto py-2 hover:bg-red-500/10 hover:text-red-400 text-gray-400"
                                            onClick={() => onAction && onAction(msg.id!, 'reject', cardValues[msg.id!] || {})}
                                        >
                                            <X className="h-4 w-4" />
                                            <span className="text-[10px]">Reject</span>
                                        </Button>

                                        <SnoozeControl onSnooze={(until) => onSnooze && onSnooze(msg.id!, until)} />
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center py-1 gap-1 opacity-70">
                                        <div className="flex items-center gap-2">
                                            {msg.status === 'executing' && <Loader2 className="h-4 w-4 text-primary animate-spin" />}
                                            {msg.status === 'approved' && <Check className="h-4 w-4 text-green-400" />}
                                            {msg.status === 'rejected' && <X className="h-4 w-4 text-red-400" />}
                                            {msg.status === 'conflict' && <Clock className="h-4 w-4 text-yellow-400" />}
                                            {msg.status === 'error' && <X className="h-4 w-4 text-red-600" />}

                                            <span className={cn(
                                                "text-sm font-bold uppercase tracking-wider",
                                                msg.status === 'executing' ? "text-primary animate-pulse" :
                                                    msg.status === 'approved' ? "text-green-400" :
                                                        msg.status === 'rejected' ? "text-red-400" :
                                                            msg.status === 'conflict' ? "text-yellow-400" :
                                                                "text-gray-400"
                                            )}>
                                                {msg.status === 'executing' ? 'Processing...' : msg.status}
                                            </span>
                                        </div>
                                        {msg.status === 'conflict' && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="mt-2 text-[10px] h-7 border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                                                onClick={() => onAction && onAction(msg.id!, 'approve', { ...(cardValues[msg.id!] || {}), force: true })}
                                            >
                                                Force Apply (Bypass Check)
                                            </Button>
                                        )}
                                        {msg.actionTimestamp && (
                                            <span className="text-[10px] text-zinc-400 mt-1 font-mono">
                                                {msg.status === 'conflict' ? 'Snapshot out of sync' : `by ${msg.actionBy || 'Unknown'}`} • {new Date(msg.actionTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

function SnoozeControl({ onSnooze }: { onSnooze: (until: number) => void }) {
    const [open, setOpen] = useState(false);
    const [customHours, setCustomHours] = useState('');

    const handleSnooze = (type: 'later' | 'tomorrow' | 'next-week') => {
        let until = Date.now();
        const now = new Date();

        switch (type) {
            case 'later':
                // +4 hours
                until = now.getTime() + (4 * 60 * 60 * 1000);
                break;
            case 'tomorrow':
                // Tomorrow at 8 AM
                const tomorrow = new Date(now);
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(8, 0, 0, 0);
                until = tomorrow.getTime();
                break;
            case 'next-week':
                // Next Monday at 8 AM (or just +7 days)
                until = now.getTime() + (7 * 24 * 60 * 60 * 1000);
                break;
        }

        onSnooze(until);
        setOpen(false);
    };

    const handleCustomSnooze = () => {
        const hours = parseFloat(customHours);
        if (isNaN(hours) || hours <= 0) return;

        const until = Date.now() + (hours * 60 * 60 * 1000);
        onSnooze(until);
        setOpen(false);
        setCustomHours('');
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className="flex flex-col items-center gap-1 h-auto py-2 hover:bg-yellow-500/10 hover:text-yellow-400 text-gray-400"
                >
                    <CalendarClock className="h-4 w-4" />
                    <span className="text-[10px]">Postpone</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[240px]" align="end">
                <Command>
                    <CommandList>
                        <CommandGroup heading="Snooze until...">
                            <CommandItem onSelect={() => handleSnooze('later')} className="cursor-pointer">
                                <Clock className="mr-2 h-4 w-4" />
                                <span className="flex-1">Later Today</span>
                                <span className="text-xs text-muted-foreground">+4h</span>
                            </CommandItem>
                            <CommandItem onSelect={() => handleSnooze('tomorrow')} className="cursor-pointer">
                                <ChevronRight className="mr-2 h-4 w-4" />
                                <span className="flex-1">Tomorrow</span>
                                <span className="text-xs text-muted-foreground">8am</span>
                            </CommandItem>
                            <CommandItem onSelect={() => handleSnooze('next-week')} className="cursor-pointer">
                                <Clock className="mr-2 h-4 w-4" />
                                <span className="flex-1">Next Week</span>
                                <span className="text-xs text-muted-foreground">+7d</span>
                            </CommandItem>
                        </CommandGroup>
                    </CommandList>
                </Command>

                <div className="p-2 border-t border-white/10 bg-white/5">
                    <div className="text-[10px] font-medium text-gray-400 mb-2 px-1 uppercase tracking-wider">Custom Duration</div>
                    <div className="flex gap-2">
                        <Input
                            type="number"
                            placeholder="Hours..."
                            value={customHours}
                            onChange={(e) => setCustomHours(e.target.value)}
                            className="h-8 text-xs bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-offset-0 focus-visible:ring-1 focus-visible:ring-white/20"
                            onKeyDown={(e) => e.key === 'Enter' && handleCustomSnooze()}
                        />
                        <Button
                            size="sm"
                            variant="secondary"
                            className="h-8 px-3 bg-white/10 hover:bg-white/20 text-white border border-white/10"
                            onClick={handleCustomSnooze}
                        >
                            Set
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
